import { Controller } from "@hotwired/stimulus"
import paper from "paper"

/**
 * Action Preview Controller
 * 
 * Renders an n8n-style flow visualization showing:
 * - Trigger nodes (left) → Action node (center) → Outlet nodes (right)
 * - For slack_message: Block mini-nodes below action
 * - Hover over nodes to see menu, click to open detail modal
 */
export default class extends Controller {
  static targets = ["canvas", "zoomLevel", "nodeMenu", "connectButton", "connectModal", "connectFrame"]
  
  static values = {
    data: Object,      // { action, triggers, outlets }
    editUrl: String,
    connectUrl: String,
    gridSize: { type: Number, default: 20 },
    zoom: { type: Number, default: 1 }
  }

  connect() {
    console.log('[ActionPreview] Connected', this.dataValue)
    this.hoveredNode = null
    this.menuVisible = false
    this.isScaled = false
    this.setupPaper()
    this.drawGrid()
    this.renderFlow()
    this.setupEventHandlers()
  }

  disconnect() {
    if (this.paperScope) {
      this.paperScope.remove()
    }
  }

  setupPaper() {
    this.paperScope = new paper.PaperScope()
    this.paperScope.setup(this.canvasTarget)
    
    this.project = this.paperScope.project
    this.view = this.paperScope.view
    
    // Layers
    this.gridLayer = new this.paperScope.Layer({ name: 'grid' })
    this.cablesLayer = new this.paperScope.Layer({ name: 'cables' })
    this.nodesLayer = new this.paperScope.Layer({ name: 'nodes' })
    
    // Track nodes
    this.nodes = new Map()
  }

  drawGrid() {
    // Dot grid pattern (like n8n)
    this.gridLayer.activate()
    const bounds = this.view.bounds
    
    for (let x = 0; x < bounds.width; x += this.gridSizeValue) {
      for (let y = 0; y < bounds.height; y += this.gridSizeValue) {
        new this.paperScope.Path.Circle({
          center: [x, y],
          radius: 1,
          fillColor: '#374151' // gray-700 for dark background
        })
      }
    }
    
    this.nodesLayer.activate()
  }

  renderFlow() {
    const { action, triggers, outlets } = this.dataValue
    const canvasWidth = this.canvasTarget.offsetWidth
    const canvasHeight = this.canvasTarget.offsetHeight
    
    // Layout calculations
    const centerX = canvasWidth / 2
    const centerY = canvasHeight / 2 - 40 // Leave room for blocks below
    
    // 1. Render trigger nodes (left side)
    const triggerList = triggers || []
    triggerList.forEach((trigger, index) => {
      const y = centerY - ((triggerList.length - 1) * 70 / 2) + (index * 70)
      const node = this.createTriggerNode(trigger, 60, y)
      this.nodes.set(`trigger-${trigger.id}`, node)
    })
    
    // 2. Render main action node (center)
    const actionNode = this.createActionNode(action, centerX - 110, centerY - 45)
    this.nodes.set('action', actionNode)
    
    // 3. Render outlet nodes (right side)
    const outletList = outlets || []
    outletList.forEach((outlet, index) => {
      const y = centerY - ((outletList.length - 1) * 55 / 2) + (index * 55)
      const node = this.createOutletNode(outlet, canvasWidth - 160, y)
      this.nodes.set(`outlet-${outlet.id}`, node)
    })
    
    // 4. Render block mini-nodes (for slack_message only)
    if (action.action_type === 'slack_message' && action.blocks?.length > 0) {
      this.renderBlockNodes(action.blocks, centerX - 110, centerY + 55)
    }
    
    // 5. Draw cables
    this.renderCables(triggerList, outletList, centerX - 110, centerY)
  }

  createTriggerNode(trigger, x, y) {
    // Create trigger node group
    const group = new this.paperScope.Group()
    group.data = { type: 'trigger', trigger }
    
    // Node card
    const card = new this.paperScope.Path.Rectangle({
      point: [x, y],
      size: [150, 55],
      radius: 8,
      fillColor: '#064e3b', // emerald-900
      strokeColor: '#10b981', // emerald-500
      strokeWidth: 2
    })
    group.addChild(card)
    
    // Icon background
    const iconBg = new this.paperScope.Path.Circle({
      center: [x + 22, y + 27],
      radius: 14,
      fillColor: '#10b981' // emerald-500
    })
    group.addChild(iconBg)
    
    // Trigger type icon (emoji for simplicity)
    const iconText = trigger.source_type === 'webhook' ? '🔗' : 
                     trigger.source_type === 'schedule' ? '⏰' : '💬'
    const icon = new this.paperScope.PointText({
      point: [x + 15, y + 32],
      content: iconText,
      fontSize: 14
    })
    group.addChild(icon)
    
    // Name
    const name = new this.paperScope.PointText({
      point: [x + 44, y + 24],
      content: this.truncateText(trigger.name, 12),
      fontSize: 11,
      fontWeight: 'bold',
      fillColor: '#ecfdf5' // emerald-50
    })
    group.addChild(name)
    
    // Parent name
    const parent = new this.paperScope.PointText({
      point: [x + 44, y + 38],
      content: this.truncateText(trigger.parent_name || trigger.source_type, 14),
      fontSize: 9,
      fillColor: '#6ee7b7' // emerald-300
    })
    group.addChild(parent)
    
    // Status indicator
    const statusColor = trigger.active ? '#10b981' : '#6b7280'
    const status = new this.paperScope.Path.Circle({
      center: [x + 140, y + 10],
      radius: 4,
      fillColor: statusColor
    })
    group.addChild(status)
    
    // Output connector
    const connector = new this.paperScope.Path.Circle({
      center: [x + 150, y + 27],
      radius: 6,
      fillColor: '#10b981',
      strokeColor: '#064e3b',
      strokeWidth: 2
    })
    connector.data = { type: 'output', nodeType: 'trigger', nodeId: trigger.id }
    group.addChild(connector)
    
    return group
  }

  createActionNode(action, x, y) {
    // Main action node - larger, more prominent
    const group = new this.paperScope.Group()
    group.data = { type: 'action', action }
    
    // Determine colors based on action type
    const colors = this.getActionTypeColors(action.action_type)
    
    // Node card with shadow effect
    const shadow = new this.paperScope.Path.Rectangle({
      point: [x + 3, y + 3],
      size: [220, 90],
      radius: 10,
      fillColor: new this.paperScope.Color(0, 0, 0, 0.3)
    })
    group.addChild(shadow)
    
    const card = new this.paperScope.Path.Rectangle({
      point: [x, y],
      size: [220, 90],
      radius: 10,
      fillColor: colors.bg,
      strokeColor: colors.border,
      strokeWidth: 2
    })
    group.addChild(card)
    
    // Icon background
    const iconBg = new this.paperScope.Path.Rectangle({
      point: [x + 14, y + 14],
      size: [44, 44],
      radius: 8,
      fillColor: colors.icon
    })
    group.addChild(iconBg)
    
    // Icon
    const iconText = this.getActionTypeIcon(action.action_type)
    const icon = new this.paperScope.PointText({
      point: [x + 26, y + 43],
      content: iconText,
      fontSize: 20,
      fillColor: 'white'
    })
    group.addChild(icon)
    
    // Name
    const name = new this.paperScope.PointText({
      point: [x + 70, y + 32],
      content: this.truncateText(action.name, 18),
      fontSize: 14,
      fontWeight: 'bold',
      fillColor: colors.text
    })
    group.addChild(name)
    
    // Subtitle (type-specific)
    let subtitle = action.type_label
    if (action.action_type === 'slack_message' && action.channel) {
      subtitle = `#${action.channel}`
    } else if (action.action_type === 'http_forward' && action.config?.url) {
      subtitle = this.truncateText(action.config.url, 24)
    }
    const subtitleText = new this.paperScope.PointText({
      point: [x + 70, y + 50],
      content: subtitle,
      fontSize: 11,
      fillColor: colors.subtitleColor
    })
    group.addChild(subtitleText)
    
    // Block count badge (for slack_message)
    if (action.action_type === 'slack_message' && action.blocks?.length > 0) {
      const badge = new this.paperScope.Path.Rectangle({
        point: [x + 70, y + 58],
        size: [55, 18],
        radius: 9,
        fillColor: colors.badgeBg
      })
      group.addChild(badge)
      
      const badgeText = new this.paperScope.PointText({
        point: [x + 78, y + 70],
        content: `${action.blocks.length} blocks`,
        fontSize: 9,
        fillColor: colors.badgeText
      })
      group.addChild(badgeText)
    }
    
    // Status indicator
    const statusColor = action.active ? '#10b981' : '#6b7280'
    const statusBg = new this.paperScope.Path.Circle({
      center: [x + 205, y + 15],
      radius: 6,
      fillColor: statusColor
    })
    group.addChild(statusBg)
    
    // Input connector (left)
    const inputConnector = new this.paperScope.Path.Circle({
      center: [x, y + 45],
      radius: 7,
      fillColor: colors.icon,
      strokeColor: colors.bg,
      strokeWidth: 2
    })
    inputConnector.data = { type: 'input', nodeType: 'action' }
    group.addChild(inputConnector)
    
    // Output connector (right)
    const outputConnector = new this.paperScope.Path.Circle({
      center: [x + 220, y + 45],
      radius: 7,
      fillColor: colors.icon,
      strokeColor: colors.bg,
      strokeWidth: 2
    })
    outputConnector.data = { type: 'output', nodeType: 'action' }
    group.addChild(outputConnector)
    
    return group
  }

  createOutletNode(outlet, x, y) {
    // Outlet node (button/select that triggers chains)
    const group = new this.paperScope.Group()
    group.data = { type: 'outlet', outlet }
    
    const colors = this.getOutletStyleColors(outlet.style)
    
    // Node card
    const card = new this.paperScope.Path.Rectangle({
      point: [x, y],
      size: [100, 48],
      radius: 6,
      fillColor: colors.bg,
      strokeColor: colors.border,
      strokeWidth: 1
    })
    group.addChild(card)
    
    // Type icon
    const iconText = outlet.outlet_type === 'button' ? '🔘' : 
                     outlet.outlet_type === 'select' ? '📋' : '☑️'
    const icon = new this.paperScope.PointText({
      point: [x + 10, y + 30],
      content: iconText,
      fontSize: 14
    })
    group.addChild(icon)
    
    // Label
    const label = new this.paperScope.PointText({
      point: [x + 30, y + 22],
      content: this.truncateText(outlet.label, 9),
      fontSize: 10,
      fontWeight: 'bold',
      fillColor: '#f3f4f6' // gray-100
    })
    group.addChild(label)
    
    // Chain target (if exists)
    if (outlet.chain_target) {
      const target = new this.paperScope.PointText({
        point: [x + 30, y + 36],
        content: `→ ${this.truncateText(outlet.chain_target, 7)}`,
        fontSize: 8,
        fillColor: '#9ca3af' // gray-400
      })
      group.addChild(target)
    } else {
      const noChain = new this.paperScope.PointText({
        point: [x + 30, y + 36],
        content: 'No chain',
        fontSize: 8,
        fillColor: '#6b7280' // gray-500
      })
      group.addChild(noChain)
    }
    
    // Input connector
    const connector = new this.paperScope.Path.Circle({
      center: [x, y + 24],
      radius: 5,
      fillColor: colors.border,
      strokeColor: colors.bg,
      strokeWidth: 2
    })
    connector.data = { type: 'input', nodeType: 'outlet', outletId: outlet.id }
    group.addChild(connector)
    
    return group
  }

  renderBlockNodes(blocks, startX, startY) {
    // Render mini block indicators below the action node
    const blockWidth = 38
    const gap = 6
    const maxVisible = 5
    const visibleBlocks = blocks.slice(0, maxVisible)
    const totalWidth = visibleBlocks.length * blockWidth + (visibleBlocks.length - 1) * gap
    const offsetX = (220 - totalWidth) / 2 // Center under action node
    
    visibleBlocks.forEach((block, index) => {
      const x = startX + offsetX + index * (blockWidth + gap)
      const y = startY
      
      const group = new this.paperScope.Group()
      group.data = { type: 'block', block, index }
      
      const style = this.getBlockStyle(block.type)
      
      // Mini card
      const card = new this.paperScope.Path.Rectangle({
        point: [x, y],
        size: [blockWidth, 30],
        radius: 4,
        fillColor: style.bgColor,
        strokeColor: style.color,
        strokeWidth: 1
      })
      group.addChild(card)
      
      // Block type icon
      const icon = new this.paperScope.PointText({
        point: [x + 14, y + 20],
        content: style.icon,
        fontSize: 12,
        fillColor: style.color
      })
      group.addChild(icon)
      
      this.nodes.set(`block-${index}`, group)
    })
    
    // Show overflow indicator if more blocks
    if (blocks.length > maxVisible) {
      const overflowX = startX + offsetX + visibleBlocks.length * (blockWidth + gap)
      const overflow = new this.paperScope.PointText({
        point: [overflowX, startY + 20],
        content: `+${blocks.length - maxVisible}`,
        fontSize: 10,
        fillColor: '#9ca3af'
      })
      this.nodesLayer.addChild(overflow)
    }
  }

  renderCables(triggers, outlets, actionX, actionY) {
    this.cablesLayer.activate()
    
    const actionInputX = actionX
    const actionInputY = actionY + 45
    const actionOutputX = actionX + 220
    const actionOutputY = actionY + 45
    
    // Cables from triggers to action
    triggers.forEach(trigger => {
      const triggerNode = this.nodes.get(`trigger-${trigger.id}`)
      if (!triggerNode) return
      
      const from = new this.paperScope.Point(
        triggerNode.bounds.right,
        triggerNode.bounds.center.y
      )
      const to = new this.paperScope.Point(actionInputX, actionInputY)
      
      this.drawCable(from, to, '#10b981') // emerald-500
    })
    
    // Cables from action to outlets
    outlets.forEach(outlet => {
      const outletNode = this.nodes.get(`outlet-${outlet.id}`)
      if (!outletNode) return
      
      const from = new this.paperScope.Point(actionOutputX, actionOutputY)
      const to = new this.paperScope.Point(
        outletNode.bounds.left,
        outletNode.bounds.center.y
      )
      
      const color = outlet.has_chain ? '#8b5cf6' : '#4b5563' // purple-500 or gray-600
      this.drawCable(from, to, color)
    })
    
    this.nodesLayer.activate()
  }

  drawCable(from, to, color) {
    // Bezier curve cable (like n8n)
    const midX = (from.x + to.x) / 2
    
    const path = new this.paperScope.Path({
      strokeColor: color,
      strokeWidth: 2,
      strokeCap: 'round'
    })
    
    path.add(from)
    path.cubicCurveTo(
      new this.paperScope.Point(midX, from.y),
      new this.paperScope.Point(midX, to.y),
      to
    )
  }

  setupEventHandlers() {
    const tool = new this.paperScope.Tool()
    
    tool.onMouseDown = (event) => {
      // If menu is visible, don't handle canvas clicks (let menu handle it)
      if (this.menuVisible) return
      
      const hitResult = this.project.hitTest(event.point, {
        fill: true,
        stroke: true,
        tolerance: 8
      })
      
      if (!hitResult) return
      
      const item = this.findParentGroup(hitResult.item)
      if (!item?.data?.type) return
      
      console.log('[ActionPreview] Node clicked:', item.data)
      
      // Open the detail modal
      this.openDetailModal(item.data)
    }
    
    // Handle hover for menu and animations
    tool.onMouseMove = (event) => {
      const hitResult = this.project.hitTest(event.point, {
        fill: true,
        tolerance: 5
      })
      
      let newHoveredNode = null
      if (hitResult) {
        const item = this.findParentGroup(hitResult.item)
        if (item?.data?.type) {
          newHoveredNode = item
        }
      }
      
      // Check if we're over the menu (don't hide if so)
      if (this.menuVisible && this.isOverMenu(event)) {
        this.canvasTarget.style.cursor = 'default'
        return
      }
      
      // Handle node transitions
      if (newHoveredNode !== this.hoveredNode) {
        // Leave old node
        if (this.hoveredNode && !this.isOverMenu(event)) {
          this.animateNodeLeave(this.hoveredNode)
          this.hideMenu()
        }
        
        // Enter new node
        if (newHoveredNode) {
          this.hoveredNode = newHoveredNode
          this.animateNodeHover(newHoveredNode)
          this.showMenu(newHoveredNode)
          this.canvasTarget.style.cursor = 'pointer'
        } else {
          this.hoveredNode = null
          this.canvasTarget.style.cursor = 'default'
        }
      } else if (newHoveredNode) {
        this.canvasTarget.style.cursor = 'pointer'
      } else {
        this.canvasTarget.style.cursor = 'default'
      }
    }
    
    // Hide menu when leaving canvas area entirely
    this.canvasTarget.addEventListener('mouseleave', () => {
      // Small delay to allow moving to menu
      setTimeout(() => {
        if (!this.isMouseOverMenu) {
          this.hideMenuAndResetNode()
        }
      }, 100)
    })
  }
  
  isOverMenu(event) {
    if (!this.hasNodeMenuTarget || !this.menuVisible) return false
    const menu = this.nodeMenuTarget
    const rect = menu.getBoundingClientRect()
    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const mouseX = canvasRect.left + event.point.x * this.zoomValue
    const mouseY = canvasRect.top + event.point.y * this.zoomValue
    return mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom
  }
  
  // Hover animation - scale up node
  animateNodeHover(node) {
    if (this.isScaled) return
    this.isScaled = true
    
    // Scale the node up from its center
    const center = node.bounds.center
    node.scale(1.05, center)
    
    // Add glow effect by adjusting stroke
    const card = node.children.find(c => c.strokeColor)
    if (card) {
      this.originalStrokeWidth = card.strokeWidth
      card.strokeWidth = 3
    }
    
    this.view.draw()
  }
  
  // Leave animation - reset scale
  animateNodeLeave(node) {
    if (!this.isScaled) return
    this.isScaled = false
    
    // Scale back down
    const center = node.bounds.center
    node.scale(1/1.05, center)
    
    // Reset stroke
    const card = node.children.find(c => c.strokeColor)
    if (card && this.originalStrokeWidth) {
      card.strokeWidth = this.originalStrokeWidth
    }
    
    this.view.draw()
  }
  
  hideMenuAndResetNode() {
    if (this.hoveredNode) {
      this.animateNodeLeave(this.hoveredNode)
      this.hoveredNode = null
    }
    this.hideMenu()
  }
  
  // Show context menu above the node
  showMenu(node) {
    if (!this.hasNodeMenuTarget) return
    
    const menu = this.nodeMenuTarget
    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const nodeBounds = node.bounds
    
    // Position menu above the node, centered
    const x = canvasRect.left + (nodeBounds.center.x * this.zoomValue) - 70
    const y = canvasRect.top + (nodeBounds.top * this.zoomValue) - 80
    
    menu.style.left = `${Math.max(10, x)}px`
    menu.style.top = `${Math.max(10, y)}px`
    menu.classList.remove('hidden')
    this.menuVisible = true
    
    // Store current node data for menu actions
    this.menuNodeData = node.data
    
    // Show/hide Connect button based on node type
    if (this.hasConnectButtonTarget) {
      const showConnect = this.nodeCanConnect(node)
      this.connectButtonTarget.classList.toggle('hidden', !showConnect)
    }
    
    // Track mouse over menu
    menu.addEventListener('mouseenter', () => { this.isMouseOverMenu = true })
    menu.addEventListener('mouseleave', () => { 
      this.isMouseOverMenu = false
      // Small delay before hiding to allow re-entering node
      setTimeout(() => {
        if (!this.isMouseOverMenu && !this.hoveredNode) {
          this.hideMenu()
        }
      }, 150)
    })
  }
  
  hideMenu() {
    if (!this.hasNodeMenuTarget) return
    this.nodeMenuTarget.classList.add('hidden')
    this.menuVisible = false
    this.menuNodeData = null
  }
  
  nodeCanConnect(node) {
    // Show "Connect" for action and outlet nodes (they can chain to other actions)
    const type = node.data?.type
    return type === 'action' || type === 'outlet'
  }
  
  // Menu action: Open detail modal
  menuOpen(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.menuNodeData) {
      this.openDetailModal(this.menuNodeData)
    }
    this.hideMenu()
  }
  
  // Menu action: Open connect wizard
  menuConnect(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.menuNodeData) {
      this.openConnectWizard(this.menuNodeData)
    }
    this.hideMenu()
  }
  
  openConnectWizard(nodeData) {
    console.log('[ActionPreview] Opening connect wizard for:', nodeData)
    
    if (!this.hasConnectModalTarget) {
      console.warn('[ActionPreview] Connect modal target not found')
      return
    }
    
    // Show the modal
    this.connectModalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
    
    // Load the form via Turbo Frame
    if (this.hasConnectFrameTarget && this.hasConnectUrlValue) {
      const sourceType = nodeData.type // 'action' or 'outlet'
      const sourceId = nodeData.type === 'outlet' ? nodeData.outlet.id : this.dataValue.action.id
      const url = `${this.connectUrlValue}?source_type=${sourceType}&source_id=${sourceId}`
      
      this.connectFrameTarget.src = url
    }
  }
  
  closeConnectModal(event) {
    if (event) {
      event.preventDefault()
    }
    
    if (this.hasConnectModalTarget) {
      this.connectModalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }
  
  stopPropagation(event) {
    event.stopPropagation()
  }

  openDetailModal(nodeData) {
    // Find the detail modal controller
    const modalController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller="detail-modal"]'),
      'detail-modal'
    )
    
    if (modalController) {
      modalController.openWithData(nodeData)
    } else {
      console.warn('[ActionPreview] Detail modal controller not found')
    }
  }

  findParentGroup(item) {
    let current = item
    while (current) {
      if (current instanceof this.paperScope.Group && current.data?.type) {
        return current
      }
      current = current.parent
    }
    return null
  }

  // Zoom controls
  zoomIn() {
    this.zoomValue = Math.min(2, this.zoomValue + 0.1)
    this.applyZoom()
  }

  zoomOut() {
    this.zoomValue = Math.max(0.5, this.zoomValue - 0.1)
    this.applyZoom()
  }

  applyZoom() {
    this.view.zoom = this.zoomValue
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = `${Math.round(this.zoomValue * 100)}%`
    }
  }

  // Helpers
  getActionTypeColors(type) {
    const colors = {
      slack_message: { 
        bg: '#1e1b4b', // indigo-950
        border: '#7c3aed', // violet-500
        icon: '#7c3aed', 
        text: '#e9d5ff', // purple-200
        subtitleColor: '#a78bfa', // violet-400
        badgeBg: '#312e81', // indigo-900
        badgeText: '#c4b5fd' // violet-300
      },
      http_forward: { 
        bg: '#172554', // blue-950
        border: '#3b82f6', // blue-500
        icon: '#3b82f6', 
        text: '#bfdbfe', // blue-200
        subtitleColor: '#60a5fa', // blue-400
        badgeBg: '#1e3a8a', // blue-900
        badgeText: '#93c5fd' // blue-300
      },
      log: { 
        bg: '#422006', // amber-950
        border: '#f59e0b', // amber-500
        icon: '#f59e0b', 
        text: '#fef3c7', // amber-100
        subtitleColor: '#fbbf24', // amber-400
        badgeBg: '#78350f', // amber-900
        badgeText: '#fcd34d' // amber-300
      },
      email: { 
        bg: '#052e16', // emerald-950
        border: '#22c55e', // emerald-500
        icon: '#22c55e', 
        text: '#dcfce7', // emerald-100
        subtitleColor: '#4ade80', // emerald-400
        badgeBg: '#14532d', // emerald-900
        badgeText: '#86efac' // emerald-300
      },
      agent: { 
        bg: '#2e1065', // purple-950
        border: '#a855f7', // purple-500
        icon: '#a855f7', 
        text: '#f3e8ff', // purple-100
        subtitleColor: '#c084fc', // purple-400
        badgeBg: '#581c87', // purple-900
        badgeText: '#d8b4fe' // purple-300
      }
    }
    return colors[type] || colors.log
  }

  getActionTypeIcon(type) {
    const icons = {
      slack_message: '⚡',
      http_forward: '🔗',
      log: '📝',
      email: '✉️',
      agent: '🤖'
    }
    return icons[type] || '⚡'
  }

  getOutletStyleColors(style) {
    const colors = {
      primary: { bg: '#1e3a8a', border: '#3b82f6' }, // blue
      danger: { bg: '#7f1d1d', border: '#ef4444' }, // red
      default: { bg: '#1f2937', border: '#4b5563' } // gray
    }
    return colors[style] || colors.default
  }

  getBlockStyle(type) {
    const styles = {
      header: { color: '#a78bfa', bgColor: '#2e1065', icon: 'H' }, // purple
      section: { color: '#60a5fa', bgColor: '#172554', icon: '§' }, // blue
      divider: { color: '#9ca3af', bgColor: '#1f2937', icon: '—' }, // gray
      context: { color: '#c084fc', bgColor: '#2e1065', icon: 'i' }, // purple
      image: { color: '#4ade80', bgColor: '#052e16', icon: '🖼' }, // emerald
      actions: { color: '#fbbf24', bgColor: '#422006', icon: '⚡' } // amber
    }
    return styles[type] || { color: '#9ca3af', bgColor: '#1f2937', icon: '?' }
  }

  truncateText(text, maxLength) {
    if (!text) return ''
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
  }
}
